/**
 * 单位搜索与入驻引导 API 服务
 * 提供搜索单位、提交入驻引导请求、管理入驻申请等接口
 *
 * 修复 P1-onboardingApi: 使用 httpGet/httpPost/httpDelete 便捷方法 + wrapResponse，
 * 删除手动 res.data.code 检查（拦截器已在 code !== 200 时抛出 ApiError）。
 * 原代码的 if (res.data.code !== 200) 永远不会命中，因为拦截器会先抛异常。
 */

import { httpGet, httpPost, httpDelete } from '@/utils/httpClient'
import { wrapResponse } from '@/services/types'
import type { ApiResponse } from '@/services/types'

const ORG_BASE = '/api/organizations'
const SETTINGS_BASE = '/api/user/settings'

// 修复单位审查#7：本域四函数原有一组 USE_MOCK 分支指向 mock/organization.ts 的 localStorage
// 假实现——管理端单位 CRUD/审批/统计早已全部直连后端，mock 分支既遮蔽真实接口又无人维护，
// 整体删除后 VITE_USE_MOCK=true 也走真实 HTTP（与系统配置域同口径）。

// =================== Types ===================

export interface OrganizationSearchResult {
  id: string
  name: string
  type: string
  region: string
}

export interface OnboardingRequestDto {
  desiredOrgName: string
  desiredOrgType?: string
  desiredRegion?: string
  contactName?: string
  contactPhone?: string
  creditCode?: string
  reason: string
}

export interface OnboardingRequestResponse {
  id: string
  orgName: string
  orgType?: string
  region?: string
  contactName?: string
  contactPhone?: string
  creditCode?: string
  reason: string
  status: string
  linkedOrgId?: string
  rejectReason?: string
  applicantId?: string
  applicantName?: string
  applicationType?: string
  createdAt: string
  updatedAt: string
}

// =================== API Functions ===================

/** 搜索单位：按名称模糊搜索，排除个人体验空间和禁用单位 */
export async function searchOrganizations(keyword: string): Promise<ApiResponse<OrganizationSearchResult[]>> {
  try {
    const data = await httpGet<{ items: OrganizationSearchResult[] }>(`${ORG_BASE}/search`, {
      params: { q: keyword },
    })
    return wrapResponse(data.items)
  } catch (e: any) {
    return { code: 400, message: e.message || '搜索失败' }
  }
}

/** 提交入驻引导请求（当目标单位不存在时） */
export async function submitOnboardingRequest(dto: OnboardingRequestDto): Promise<ApiResponse<OnboardingRequestResponse>> {
  try {
    const data = await httpPost<OnboardingRequestResponse>(`${SETTINGS_BASE}/onboarding-requests`, dto)
    return wrapResponse(data)
  } catch (e: any) {
    return { code: 400, message: e.message || '提交失败' }
  }
}

/** 获取我的入驻引导请求列表 */
export async function getMyOnboardingRequests(): Promise<ApiResponse<OnboardingRequestResponse[]>> {
  try {
    const data = await httpGet<OnboardingRequestResponse[]>(`${SETTINGS_BASE}/onboarding-requests`)
    return wrapResponse(data)
  } catch (e: any) {
    return { code: 400, message: e.message || '获取列表失败' }
  }
}

/** 撤销入驻引导请求 */
export async function cancelOnboardingRequest(id: string): Promise<ApiResponse<void>> {
  try {
    await httpDelete(`${SETTINGS_BASE}/onboarding-requests/${id}`)
    return wrapResponse(undefined as void)
  } catch (e: any) {
    return { code: 400, message: e.message || '撤销失败' }
  }
}
